import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SystemConfigService } from '../../services/systemConfigService';
import { SystemConfig } from '../../types';
import { migrateUserSearchIndex } from '../../utils/migrateUserSearchIndex';
import { useAuth } from '../../contexts/AuthContext';

/**
 * MentionSettings Component
 * Admin page for managing mention system settings
 *
 * Features:
 * - Enable/disable mentions for different content types
 * - Configure mention limits and search parameters
 * - Run user search index migration
 * - Reset to default settings
 */
const MentionSettings: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();

  // State
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [migrationResult, setMigrationResult] = useState<{ success: number; failed: number } | null>(null);

  // Settings state
  const [enableMessageMentions, setEnableMessageMentions] = useState(true);
  const [enableNoteMentions, setEnableNoteMentions] = useState(true);
  const [enableMemoryMentions, setEnableMemoryMentions] = useState(true);
  const [enableCommentMentions, setEnableCommentMentions] = useState(true);
  const [enableFeatureRequestMentions, setEnableFeatureRequestMentions] = useState(true);
  const [maxMentionsPerContent, setMaxMentionsPerContent] = useState(10);
  const [searchMinCharacters, setSearchMinCharacters] = useState(1);
  const [searchMaxResults, setSearchMaxResults] = useState(10);
  const [searchDebounceMs, setSearchDebounceMs] = useState(300);

  /**
   * Load system config on mount
   */
  useEffect(() => {
    loadConfig();
  }, []);

  /**
   * Load current system configuration
   */
  const loadConfig = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const systemConfig = await SystemConfigService.getConfig();
      setConfig(systemConfig);

      // Set form values from config
      setEnableMessageMentions(systemConfig.content.enableMessageMentions ?? true);
      setEnableNoteMentions(systemConfig.content.enableNoteMentions ?? true);
      setEnableMemoryMentions(systemConfig.content.enableMemoryMentions ?? true);
      setEnableCommentMentions(systemConfig.content.enableCommentMentions ?? true);
      setEnableFeatureRequestMentions(systemConfig.content.enableFeatureRequestMentions ?? true);
      setMaxMentionsPerContent(systemConfig.content.maxMentionsPerContent ?? 10);
      setSearchMinCharacters(systemConfig.content.searchMinCharacters ?? 1);
      setSearchMaxResults(systemConfig.content.searchMaxResults ?? 10);
      setSearchDebounceMs(systemConfig.content.searchDebounceMs ?? 300);
    } catch (err: any) {
      console.error('Error loading config:', err);
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save settings to system config
   */
  const handleSave = async (): Promise<void> => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      if (!config) {
        throw new Error('Configuration not loaded');
      }

      // Update config with new values
      const updatedConfig: SystemConfig = {
        ...config,
        content: {
          ...config.content,
          enableMessageMentions,
          enableNoteMentions,
          enableMemoryMentions,
          enableCommentMentions,
          enableFeatureRequestMentions,
          maxMentionsPerContent,
          searchMinCharacters,
          searchMaxResults,
          searchDebounceMs,
        },
      };

      if (!authState.user) {
        throw new Error('User not authenticated');
      }

      const savedConfig = await SystemConfigService.updateConfig(
        updatedConfig,
        authState.user.uid,
        authState.user.displayName || authState.user.email || 'Admin'
      );
      setConfig(savedConfig);
      setSuccessMessage('Settings saved successfully');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Reset to default settings
   */
  const handleReset = (): void => {
    setEnableMessageMentions(true);
    setEnableNoteMentions(true);
    setEnableMemoryMentions(true);
    setEnableCommentMentions(true);
    setEnableFeatureRequestMentions(true);
    setMaxMentionsPerContent(10);
    setSearchMinCharacters(1);
    setSearchMaxResults(10);
    setSearchDebounceMs(300);
  };

  /**
   * Run user search index migration
   */
  const handleRunMigration = async (): Promise<void> => {
    if (!window.confirm('This will index all users for mention search. Continue?')) {
      return;
    }

    try {
      setMigrating(true);
      setError(null);
      setSuccessMessage(null);
      setMigrationResult(null);

      const result = await migrateUserSearchIndex();
      setMigrationResult(result);
      setSuccessMessage(`Migration completed: ${result.success} users indexed, ${result.failed} failed`);
    } catch (err: any) {
      console.error('Error running migration:', err);
      setError(err.message || 'Failed to run migration');
    } finally {
      setMigrating(false);
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Mention Settings
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Configure mention system behavior and search parameters
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Feature Toggles */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Feature Toggles
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableMessageMentions}
                    onChange={(e) => setEnableMessageMentions(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Enable Message Mentions"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableNoteMentions}
                    onChange={(e) => setEnableNoteMentions(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Enable Note Mentions"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableMemoryMentions}
                    onChange={(e) => setEnableMemoryMentions(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Enable Memory Mentions"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableCommentMentions}
                    onChange={(e) => setEnableCommentMentions(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Enable Comment Mentions"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableFeatureRequestMentions}
                    onChange={(e) => setEnableFeatureRequestMentions(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Enable Feature Request Mentions"
              />
            </Stack>
          </Paper>
        </Grid>

        {/* Search Parameters */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Search Parameters
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <TextField
                label="Max Mentions Per Content"
                type="number"
                value={maxMentionsPerContent}
                onChange={(e) => setMaxMentionsPerContent(parseInt(e.target.value) || 10)}
                disabled={saving}
                helperText="Maximum number of mentions allowed in a single piece of content"
                fullWidth
                inputProps={{ min: 1, max: 50 }}
              />
              <TextField
                label="Search Min Characters"
                type="number"
                value={searchMinCharacters}
                onChange={(e) => setSearchMinCharacters(parseInt(e.target.value) || 1)}
                disabled={saving}
                helperText="Minimum characters required to trigger search"
                fullWidth
                inputProps={{ min: 0, max: 5 }}
              />
              <TextField
                label="Search Max Results"
                type="number"
                value={searchMaxResults}
                onChange={(e) => setSearchMaxResults(parseInt(e.target.value) || 10)}
                disabled={saving}
                helperText="Maximum number of search results to display"
                fullWidth
                inputProps={{ min: 1, max: 50 }}
              />
              <TextField
                label="Search Debounce (ms)"
                type="number"
                value={searchDebounceMs}
                onChange={(e) => setSearchDebounceMs(parseInt(e.target.value) || 300)}
                disabled={saving}
                helperText="Delay before triggering search after typing"
                fullWidth
                inputProps={{ min: 0, max: 2000, step: 100 }}
              />
            </Stack>
          </Paper>
        </Grid>

        {/* User Search Index Migration */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Search Index Migration
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Card sx={{ bgcolor: 'background.default', mb: 2 }}>
              <CardContent>
                <Typography variant="body2" paragraph>
                  Run this migration to populate the user search index for all existing users.
                  This is required for the mention autocomplete feature to work.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> This operation may take several minutes depending on the number of users.
                  It is safe to run multiple times.
                </Typography>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              color="primary"
              startIcon={migrating ? <CircularProgress size={20} /> : <RunIcon />}
              onClick={handleRunMigration}
              disabled={migrating}
            >
              {migrating ? 'Running Migration...' : 'Run Migration'}
            </Button>

            {migrationResult && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Migration completed successfully!
                <br />
                <strong>{migrationResult.success}</strong> users indexed
                {migrationResult.failed > 0 && (
                  <>
                    <br />
                    <strong>{migrationResult.failed}</strong> users failed
                  </>
                )}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Defaults
        </Button>
      </Box>
    </Container>
  );
};

export default MentionSettings;
